import { useCallback, useEffect, useState } from 'react';

import { Button, CircularProgress, Stack, TextField } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';

import { BasicButton } from 'components/atoms/BasicButton';
import { SVGAsset } from 'components/atoms/SVGAsset';
import { Typography } from 'components/atoms/Typography';
import useAxios from 'hooks/api/useAxios';
import { imageFileStorage } from 'types/api/imageFileStorage';

import { DropZoneWrapper } from '../../FileUploadControl/FileUploadControl.styled';

type ImageUploadControlProps = {
    onSuccess?: (file: imageFileStorage) => void;
    onClose?: () => void;
};

export const ImageUploadControl = ({
    onSuccess = () => undefined,
    onClose = () => undefined,
}: ImageUploadControlProps): JSX.Element => {
    const [body, setBody] = useState<FormData>();
    const [uploadedFile, setUploadedFile] = useState<string | Blob>();

    const {
        register,
        formState: {
            errors: { name: nameErr },
        },
        handleSubmit,
    } = useForm<{ name: string }>();

    const [res, { rerun }] = useAxios<imageFileStorage>(
        {
            url: '/api/builder/image/add/',
            method: 'POST',
            data: body,
            skipRequest: () => true,
        },
        [body]
    );

    const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
        if (acceptedFiles?.length === 1) {
            setUploadedFile(acceptedFiles[0]);
        }
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': [],
            'image/png': [],
        },
    });

    const onSubmit = handleSubmit((data) => {
        const fData = new FormData();

        fData.append('name', data.name || '');
        fData.append('file', uploadedFile || '');

        setBody(fData);
        rerun();
    });

    useEffect(() => {
        if (res.type === 'success') {
            onSuccess(res.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [res.type, res.data]);

    const fileTooLarge =
        res.type === 'error' && res.data.response?.status === 429;

    return (
        <Stack sx={{ flex: 1 }}>
            {res.type === 'idle' ? (
                <Stack component="form" onSubmit={onSubmit} sx={{ flex: 1 }}>
                    {!uploadedFile ? (
                        <DropZoneWrapper {...getRootProps()} sx={{ flex: 1 }}>
                            <input {...getInputProps()} />
                            <Stack spacing={4} pt="72px">
                                <Typography
                                    variant="title.large"
                                    $color="common.grey"
                                >
                                    Перетащите файл сюда
                                </Typography>
                                <BasicButton $bgColor="green" type="button">
                                    Выбрать файл
                                </BasicButton>
                            </Stack>
                        </DropZoneWrapper>
                    ) : (
                        <>
                            <TextField
                                {...register('name', {
                                    required: true,
                                    pattern: /^[^\s]+(?:$|.*[^\s]+$)/,
                                })}
                                label="Введите название"
                                error={!!nameErr}
                                size="small"
                            />
                            <img
                                src={URL.createObjectURL(uploadedFile as Blob)}
                                alt=""
                                style={{
                                    maxHeight: 390,
                                    minHeight: 390,
                                    marginTop: '8px',
                                    objectFit: 'contain',
                                }}
                            />
                        </>
                    )}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mt: 2 }}
                    >
                        <Button variant="text" onClick={() => onClose()}>
                            Назад к списку
                        </Button>
                        {uploadedFile ? (
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={!!nameErr}
                            >
                                Сохранить изображение
                            </Button>
                        ) : undefined}
                    </Stack>
                </Stack>
            ) : undefined}
            {res.type === 'loading' ? (
                <Stack
                    sx={{ flex: 1 }}
                    justifyContent="center"
                    alignItems="center"
                >
                    <CircularProgress variant="indeterminate" />
                </Stack>
            ) : undefined}
            {res.type === 'error' ? (
                <Stack
                    spacing={4}
                    sx={{ flex: 1 }}
                    justifyContent="center"
                    alignItems="center"
                >
                    <SVGAsset src="icons.56px.negative" />
                    <Typography variant="title.large" $color="common.grey">
                        {fileTooLarge
                            ? 'Слишком большой размер файла'
                            : 'Не удалось загрузить файл'}
                    </Typography>
                    <BasicButton
                        style={{ width: 'auto' }}
                        $bgColor="green"
                        onClick={() => (fileTooLarge ? onClose() : rerun())}
                    >
                        {fileTooLarge ? 'Все понятно' : 'Попробовать еще раз'}
                    </BasicButton>
                    {!fileTooLarge ? (
                        <BasicButton
                            style={{ width: 'auto' }}
                            $bgColor="red"
                            onClick={() => onClose()}
                        >
                            Отмена
                        </BasicButton>
                    ) : undefined}
                </Stack>
            ) : undefined}
        </Stack>
    );
};
